import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, X, MapPin, Search, AlertCircle } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Checkbox } from './ui/checkbox';
import { CommanServices } from '@/services/commanServices';
import { filterAndRankLocations } from '@/utils/locationSearch';


const LocationSelector = ({
  value = null, // single: string, multi: array
  onChange = () => {},
  mode = 'single', // 'single' | 'multi'
  placeholder = 'Select location...',
  allowWildcard = false, // Enable "All Locations" wildcard option
  className = '',
  disabled = false,
  dark = false
}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fetchTimeout, setFetchTimeout] = useState(null);
  const [lastFetch, setLastFetch] = useState(0);
  
  const CACHE_DURATION = 30000; // 30 seconds cache
  const DEBOUNCE_DELAY = 500; // 500ms debounce

  // Debounced fetch locations to prevent excessive calls
  useEffect(() => {
    // Clear existing timeout
    if (fetchTimeout) {
      clearTimeout(fetchTimeout);
    }
    
    // Set new debounced timeout
    const timeout = setTimeout(() => {
      fetchLocations();
    }, DEBOUNCE_DELAY);
    
    setFetchTimeout(timeout);
    
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, []); // Only on mount

  const fetchLocations = async () => {
    try {
      const now = Date.now();
      
      // Return cached data if still valid and locations exist
      if (locations.length > 0 && (now - lastFetch) < CACHE_DURATION) {
        console.log('🚀 LocationSelector: Using cached locations (performance optimization)');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      console.log('🔗 LocationSelector: Fetching locations (debounced)');
      const response = await CommanServices.getlocationszonesdata();
      if (response.status) {
        const data = await response.data;
        setLocations(data || []);
        setLastFetch(now);
        console.log('✅ LocationSelector: Fetched', (data.locations || []).length, 'locations with caching');
      }
      
    } catch (err) {
      console.error('❌ LocationSelector: Error fetching locations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search with 350ms delay
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const locationList = useMemo(() => {
    if (Array.isArray(locations)) return locations;
    return locations?.locations || locations?.zones || [];
  }, [locations]);

  const filteredLocations = useMemo(() => {
    if (!locationList.length) return [];
    return filterAndRankLocations(locationList, debouncedSearchTerm, {
      allLocations: locationList,
    });
  }, [locationList, debouncedSearchTerm]);

  // Get selected locations for display
  const selectedLocations = useMemo(() => {
    if (mode === 'single') {
      return value ? locationList.find(loc => loc.id === value) : null;
    } else {
      return (value && value.length > 0) ? locationList.filter(loc => value.includes(loc.id)) : [];
    }
  }, [value, locationList, mode]);

  // Handle selection
  const handleSelect = (locationId) => {
    if (mode === 'single') {
      onChange(locationId);
      setOpen(false);
    } else {
      const currentValue = value || [];
      const newValue = currentValue.includes(locationId)
        ? currentValue.filter(v => v !== locationId)
        : [...currentValue, locationId];
      onChange(newValue);
    }
  };

  // Handle wildcard selection
  const handleWildcardSelect = () => {
    if (mode === 'single') {
      onChange(''); // Empty string = wildcard
      setOpen(false);
    } else {
      onChange([]); // Empty array = wildcard
      setOpen(false);
    }
  };

  // Remove selected item (multi-select)
  const removeSelection = (locationId, e) => {
    e.stopPropagation();
    if (mode === 'multi') {
      const newValue = (value || []).filter(v => v !== locationId);
      onChange(newValue);
    }
  };

  // Handle Select All / Deselect All functionality
  const handleSelectAll = () => {
    if (mode !== 'multi' || !filteredLocations.length) return;
    
    const currentValue = value || [];
    const filteredIds = filteredLocations.map(loc => loc.id);
    
    // Check if all filtered locations are already selected
    const allFilteredSelected = filteredIds.every(id => currentValue.includes(id));
    
    if (allFilteredSelected) {
      // Deselect all filtered items
      const newValue = currentValue.filter(id => !filteredIds.includes(id));
      onChange(newValue);
    } else {
      // Select all filtered items (merge with existing selections)
      const newValue = [...new Set([...currentValue, ...filteredIds])];
      onChange(newValue);
    }
  };

  // Get Select All button text and state
  const getSelectAllButtonState = () => {
    if (mode !== 'multi' || !filteredLocations.length) {
      return {
        text: 'Select All (0)',
        disabled: true,
        tooltip: 'No results to select'
      };
    }
    
    const currentValue = value || [];
    const filteredIds = filteredLocations.map(loc => loc.id);
    const allFilteredSelected = filteredIds.every(id => currentValue.includes(id));
    const count = filteredIds.length;
    
    return {
      text: allFilteredSelected ? `Deselect All (${count})` : `Select All (${count})`,
      disabled: false,
      tooltip: allFilteredSelected ? `Deselect all ${count} filtered results` : `Select all ${count} filtered results`
    };
  };

  // Display text for trigger button
  const getDisplayText = () => {
    if (allowWildcard && (!value || (Array.isArray(value) && value.length === 0))) {
      return '🌟 All Locations (wildcard)';
    }

    if (mode === 'single') {
      if (selectedLocations) {
        // Show Auto-Generated Label with fallback
        return selectedLocations.auto_label || `${selectedLocations.zone_name} – ${selectedLocations.building} – ${selectedLocations.floor}${selectedLocations.room ? ` – ${selectedLocations.room}` : ''}`;
      }
      return placeholder;
    } else {
      const count = selectedLocations.length;
      if (count === 0) {
        // In multi-mode, if no selections and wildcard is allowed, show wildcard text
        return allowWildcard ? '🌟 All Locations (wildcard)' : placeholder;
      }
      if (count === 1) {
        // Show Auto-Generated Label with fallback for single selection in multi-mode
        return selectedLocations[0].auto_label || `${selectedLocations[0].zone_name} – ${selectedLocations[0].building} – ${selectedLocations[0].floor}${selectedLocations[0].room ? ` – ${selectedLocations[0].room}` : ''}`;
      }
      return `${count} locations selected`;
    }
  };

  // Render status badge
  const getStatusBadge = (status) => {
    const colors = {
      normal: 'bg-green-100 text-green-700',
      maintenance: 'bg-yellow-100 text-yellow-700',
      closed: 'bg-red-100 text-red-700',
      restricted: 'bg-orange-100 text-orange-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 p-2 border rounded-md bg-gray-50 ${className}`}>
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span className="text-sm text-gray-600">Loading locations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center space-x-2 p-2 border border-red-200 rounded-md bg-red-50 ${className}`}>
        <AlertCircle className="h-4 w-4 text-red-500" />
        <span className="text-sm text-red-600">Error loading locations: {error}</span>
        <Button variant="ghost" size="sm" onClick={fetchLocations}>
          Retry
        </Button>
      </div>
    );
  }

  const pop = dark ? 'z-[1300] bg-[#0d1128] border-[rgba(168,133,255,0.2)] text-[#e6e9f6] [&_[cmdk-item][data-selected=true]]:bg-[rgba(168,85,247,0.16)] [&_[cmdk-item][data-selected=true]]:text-white [&_[cmdk-input-wrapper]]:border-[rgba(150,165,210,0.14)]' : '';
  const cmd = dark ? 'bg-transparent text-[#e6e9f6]' : '';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between ${dark ? 'bg-[rgba(7,10,28,0.6)] border-[rgba(150,165,210,0.2)] rounded-[11px] text-white hover:bg-[rgba(7,10,28,0.8)] hover:border-[#a855f7]/50 hover:text-white' : ''} ${className}`}
          disabled={disabled}
        >
          <div className="flex items-center space-x-2 flex-1 text-left">
            <MapPin className={`h-4 w-4 ${dark ? 'text-[#c084fc]' : 'text-gray-500'}`} />
            <span className={`truncate ${dark && !selectedLocations ? 'text-[#4a5378]' : ''}`}>{getDisplayText()}</span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className={`w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0 ${pop}`} align="start">
        <Command shouldFilter={false} className={cmd}>
          <CommandInput
            placeholder="Search locations..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className={dark ? 'text-white placeholder:text-[#4a5378]' : ''}
          />
          {mode === 'multi' && (
            <div className="flex justify-end px-2 py-1.5 border-b">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs px-2 py-1 h-7 whitespace-nowrap"
                onClick={handleSelectAll}
                disabled={getSelectAllButtonState().disabled}
                title={getSelectAllButtonState().tooltip}
              >
                {getSelectAllButtonState().text}
              </Button>
            </div>
          )}

          <CommandList
            className={`max-h-[280px] overflow-y-auto ${dark ? 'no-scrollbar' : ''}`}
            onWheel={(e) => { e.currentTarget.scrollTop += e.deltaY; }}
          >
            {/* Multi-select: Show selected items as removable chips at top */}
            {mode === 'multi' && selectedLocations.length > 0 && (
              <div className="p-2 border-b bg-blue-50">
                <div className="text-xs font-medium text-blue-800 mb-2">Selected ({selectedLocations.length}):</div>
                <div className="flex flex-wrap gap-1">
                  {selectedLocations.map((location) => (
                    <Badge 
                      key={location.id || `${location.zone_name}-${location.building}-${location.floor}-${location.room}`} 
                      variant="secondary" 
                      className="text-xs bg-blue-100 text-blue-800"
                    >
                      {location.auto_label || `${location.zone_name} – ${location.building} – ${location.floor}${location.room ? ` – ${location.room}` : ''}`}
                      <X 
                        className="ml-1 h-3 w-3 cursor-pointer hover:text-red-600" 
                        onClick={(e) => removeSelection(location.id, e)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Wildcard option */}
            {allowWildcard && (
              <CommandGroup heading="Wildcard Option">
                <CommandItem onSelect={handleWildcardSelect}>
                  <div className="flex items-center space-x-2 w-full">
                    <div className="w-4 h-4 flex items-center justify-center">
                      {(!value || (Array.isArray(value) && value.length === 0)) && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-blue-700">🌟 All Locations (wildcard)</div>
                      <div className="text-xs text-blue-600">Apply to all current and future locations</div>
                    </div>
                  </div>
                </CommandItem>
              </CommandGroup>
            )}

            {/* Empty state */}
            {filteredLocations.length === 0 && !loading && (
              <CommandEmpty>
                <div className="text-center py-6">
                  <MapPin className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <div className="text-sm font-medium text-gray-900 mb-1">
                    {debouncedSearchTerm ? 'No location found' : 'No locations yet'}
                  </div>
                  <div className="text-xs text-gray-500 mb-3">
                    {debouncedSearchTerm
                      ? 'Try a different spelling, partial name, or fewer words'
                      : 'Create locations to populate this dropdown'}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      // Navigate to Locations & Zones - assuming this is available in parent context
                      window.location.hash = '#locations';
                    }}
                  >
                    <MapPin className="w-3 h-3 mr-1" />
                    Settings → Locations & Zones
                  </Button>
                </div>
              </CommandEmpty>
            )}

            {/* Flat location options (no grouping) */}
            {filteredLocations.length > 0 && (
              <CommandGroup>
                {filteredLocations.map((location) => {
                  const isSelected = mode === 'single' 
                    ? value === location.id
                    : (value || []).includes(location.id);

                  return (
                    <CommandItem 
                      key={location.id || `${location.zone_name}-${location.building}-${location.floor}-${location.room}`}
                      value={`${location.zone_name} ${location.auto_label} ${location.zone_type} ${location.building} ${location.floor} ${location.room || ''}`}
                      onSelect={() => handleSelect(location.id)}
                    >
                      <div className="flex items-center space-x-2 w-full">
                        {mode === 'multi' ? (
                          <Checkbox 
                            checked={isSelected}
                            className="h-4 w-4"
                          />
                        ) : (
                          <div className="w-4 h-4 flex items-center justify-center">
                            {isSelected && (
                              <div className={`w-2 h-2 rounded-full ${dark ? 'bg-[#c084fc]' : 'bg-blue-600'}`}></div>
                            )}
                          </div>
                        )}
                        
                        <div className="flex-1 min-w-0">
                          {/* Primary: Auto-Generated Label (main display with fallback) */}
                          <div className={`font-semibold truncate text-sm ${dark ? 'text-[#e6e9f6]' : 'text-gray-900'}`}>
                            {location.auto_label || `${location.zone_name} – ${location.building} – ${location.floor}${location.room ? ` – ${location.room}` : ''}`}
                          </div>
                          {/* Secondary: Description if available */}
                          {location.description && (
                            <div className={`text-xs truncate mt-0.5 ${dark ? 'text-[#8a92b0]' : 'text-gray-600'}`}>
                              {location.description}
                            </div>
                          )}
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge variant="outline" className={`text-xs ${dark ? 'bg-[rgba(7,10,28,0.6)] border-[rgba(150,165,210,0.25)] text-[#cbd2e8]' : ''}`}>
                              {location.zone_type}
                            </Badge>
                            <Badge className={`text-xs ${dark ? 'bg-[rgba(168,85,247,0.15)] text-[#d8b4fe] border border-[rgba(168,85,247,0.3)]' : getStatusBadge(location.status)}`}>
                              {location.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>

          {/* Helper text */}
          {allowWildcard && (
            <div className="border-t p-2 bg-gray-50">
              <div className="text-xs text-gray-600">
                💡 Leave blank to apply to all locations
              </div>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LocationSelector;